import { ATOMIC_AMOUNT_PATTERN, type Money, type SignedMoney } from '@urbanrats/contracts';
import { err, ok, type Result } from './result.ts';

export type MoneyError =
  | { code: 'INVALID_ATOMIC'; input: string }
  | { code: 'INVALID_DECIMAL'; input: string }
  | { code: 'PRECISION_OVERFLOW'; input: string; scale: number }
  | { code: 'NEGATIVE'; input: string }
  | { code: 'AMBIGUOUS_SEPARATORS'; input: string }
  | { code: 'TOO_LARGE'; input: string }
  | { code: 'CURRENCY_MISMATCH'; a: string; b: string };

/** Parses a non-negative atomic amount string. Rejects signs, exponents, whitespace, decimals. */
export function parseAtomic(input: string): Result<bigint, MoneyError> {
  if (typeof input !== 'string' || !ATOMIC_AMOUNT_PATTERN.test(input)) return err({ code: 'INVALID_ATOMIC', input: String(input) });
  return ok(BigInt(input));
}

export function atomicOf(m: Money): bigint {
  const r = parseAtomic(m.amountAtomic);
  if (!r.ok) throw new RangeError(`invalid amountAtomic ${m.amountAtomic}`);
  return r.value;
}

export function money(amount: bigint, currency: string, scale: number): Money {
  if (amount < 0n) throw new RangeError('Money cannot be negative; use signedMoney');
  return { amountAtomic: amount.toString(10), currency, scale };
}

export function signedMoney(amount: bigint, currency: string, scale: number): SignedMoney {
  return { amountAtomic: amount.toString(10), currency, scale };
}

export function sameUnit(a: Pick<Money, 'currency' | 'scale'>, b: Pick<Money, 'currency' | 'scale'>): boolean {
  return a.currency === b.currency && a.scale === b.scale;
}

export function addMoney(a: Money, b: Money): Result<Money, MoneyError> {
  if (!sameUnit(a, b)) return err({ code: 'CURRENCY_MISMATCH', a: `${a.currency}/${a.scale}`, b: `${b.currency}/${b.scale}` });
  return ok(money(atomicOf(a) + atomicOf(b), a.currency, a.scale));
}

/**
 * Parses a user-entered decimal using '.' as the decimal separator (locale handling lives in the UI layer
 * and must normalise to this form). No rounding: more fractional digits than `scale` is an error.
 */
export function parseDecimalToAtomic(input: string, scale: number): Result<bigint, MoneyError> {
  const s = input.trim();
  if (s.startsWith('-')) return err({ code: 'NEGATIVE', input });
  const m = /^(0|[1-9][0-9]*)(?:\.([0-9]+))?$/.exec(s);
  if (!m) return err({ code: 'INVALID_DECIMAL', input });
  const whole = m[1] ?? '0';
  const frac = m[2] ?? '';
  if (frac.length > scale) return err({ code: 'PRECISION_OVERFLOW', input, scale });
  return ok(BigInt(whole + frac.padEnd(scale, '0')));
}

/** Exact fixed-point rendering, e.g. (100000n, 8) -> "0.00100000". No float conversion. */
export function formatAtomic(amount: bigint, scale: number): string {
  const neg = amount < 0n;
  const digits = (neg ? -amount : amount).toString(10).padStart(scale + 1, '0');
  const whole = digits.slice(0, digits.length - scale);
  const frac = scale > 0 ? `.${digits.slice(digits.length - scale)}` : '';
  return `${neg ? '-' : ''}${whole}${frac}`;
}

export function formatMoney(m: Money | SignedMoney): string {
  return `${formatAtomic(BigInt(m.amountAtomic), m.scale)} ${m.currency}`;
}

/** Largest atomic amount the contracts carry (40 digits). */
const MAX_ATOMIC_DIGITS = 40;

export interface DecimalSeparators {
  decimal: string;
  group: string;
}

/**
 * Parses what a player typed or pasted, in their locale, to atomic units. Fixed-point only:
 * no rounding, no floats. Accepted: digits, one decimal separator, grouping in blocks of three, spaces.
 * Rejected with a reason: signs, exponents, NaN/∞, letters, currency symbols, a group separator after
 * the decimal separator (e.g. "1.234,56" in an English locale), malformed grouping ("1.5" in Turkish is
 * not read as 15), more fractional digits than the wallet scale, amounts beyond 40 atomic digits.
 */
export function parseLocaleDecimal(input: string, sep: DecimalSeparators, scale: number): Result<bigint, MoneyError> {
  // Spaces, NBSP and narrow NBSP are grouping in many locales (fr, ru, sv).
  let s = input.replace(/[\s\u00a0\u202f]/g, '');
  if (s.startsWith('-') || s.startsWith('\u2212')) return err({ code: 'NEGATIVE', input });
  if (s === '' || !/^[0-9.,'\u066b\u066c]+$/.test(s)) return err({ code: 'INVALID_DECIMAL', input });
  const group = sep.group === '\u00a0' || sep.group === '\u202f' ? '' : sep.group;
  const dec = s.lastIndexOf(sep.decimal);
  if (group && s.indexOf(group) !== -1 && dec !== -1 && s.indexOf(group, dec) !== -1) return err({ code: 'AMBIGUOUS_SEPARATORS', input });
  let whole = dec === -1 ? s : s.slice(0, dec);
  const frac = dec === -1 ? '' : s.slice(dec + sep.decimal.length);
  if (group && whole.includes(group)) {
    if (!new RegExp(`^[0-9]{1,3}(${escapeRe(group)}[0-9]{3})+$`).test(whole)) return err({ code: 'AMBIGUOUS_SEPARATORS', input });
    whole = whole.split(group).join('');
  }
  if (!/^[0-9]*$/.test(whole) || !/^[0-9]*$/.test(frac) || (whole === '' && frac === '')) return err({ code: 'INVALID_DECIMAL', input });
  s = `${whole.replace(/^0+(?=[0-9])/, '') || '0'}${frac ? `.${frac}` : ''}`;
  const r = parseDecimalToAtomic(s, scale);
  if (r.ok && r.value.toString().length > MAX_ATOMIC_DIGITS) return err({ code: 'TOO_LARGE', input });
  return r.ok ? r : err({ ...r.error, input } as MoneyError);
}

const escapeRe = (x: string) => x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
